import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';
import { getSectionTheme, getLightSectionTheme } from '../lib/sectionThemes';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { HeroAccents, healthHeroTheme } from '../components/layout/HeroAccents';
import PageSubNav from '../components/layout/PageSubNav';
import DataAsOf from '../components/shared/DataAsOf';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PerformanceOverview from '../components/performance/PerformanceOverview';
import PerformanceOutcomes from '../components/performance/PerformanceOutcomes';
import PerformanceDrivers from '../components/performance/PerformanceDrivers';
import PerformanceCapabilities from '../components/performance/PerformanceCapabilities';
import PerformanceCohorts from '../components/performance/PerformanceCohorts';
import PerformanceInfluence from '../components/performance/PerformanceInfluence';
import PerformanceEvidence from '../components/performance/PerformanceEvidence';
import PerformanceHistory from '../components/performance/PerformanceHistory';

const SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/performance-health', end: true },
  { id: 'outcomes', label: 'Outcomes', to: '/performance-health/outcomes' },
  { id: 'drivers', label: 'Drivers & Constraints', to: '/performance-health/drivers' },
  { id: 'capabilities', label: 'Capabilities', to: '/performance-health/capabilities' },
  { id: 'cohorts', label: 'Cohorts', to: '/performance-health/cohorts' },
  { id: 'influence', label: 'Influence', to: '/performance-health/influence' },
  { id: 'evidence', label: 'Evidence', to: '/performance-health/evidence' },
  { id: 'history', label: 'History', to: '/performance-health/history' },
];
const PENDING = {};

// Performance Health — sibling of Transfer Health: are sustained behaviors producing the business outcomes we expect?
export default function PerformanceHealthPage() {
  const { viewAsOrg, isSuperAdmin, rbac } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const urlOrgId = useDeepLinkOrg();
  const location = useLocation();
  const subView = location.pathname.split('/')[2] || 'overview';
  const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || '');
  const [selectedCohort, setSelectedCohort] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => { if (isSuperAdmin) api.get('/rbac/organizations').then((r) => setOrganizations(r.data.organizations || [])).catch(() => {}); }, [isSuperAdmin]);
  useEffect(() => { if (urlOrgId) setSelectedOrg(urlOrgId); }, [urlOrgId]);
  useEffect(() => {
    const q = selectedOrg ? `?organization_id=${encodeURIComponent(selectedOrg)}` : '';
    api.get(`/segments/cohorts${q}`).then((r) => setCohorts(r.data.cohorts || [])).catch(() => setCohorts([]));
  }, [selectedOrg]);
  const theme = healthHeroTheme(isDark, getSectionTheme, getLightSectionTheme);
  return (
    <PageShell theme={theme} hero={(
      <PageHero title="Performance Health" titleHint="PH" description="Are sustained behaviors producing the business outcomes we expect?" theme={theme} testId="ph-hero"
        actions={(
          <div className="flex flex-wrap gap-2" data-testid="ph-toolbar">
            {isSuperAdmin && <Select value={selectedOrg || '_all'} onValueChange={(v) => { setSelectedOrg(v === '_all' ? '' : v); setSelectedCohort(''); }}><SelectTrigger className="h-8 w-52 text-xs" data-testid="ph-org-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="_all">All Organizations</SelectItem>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>}
            <Select value={selectedCohort || '_all'} onValueChange={(v) => setSelectedCohort(v === '_all' ? '' : v)}><SelectTrigger className="h-8 w-48 text-xs" data-testid="ph-cohort-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="_all">All Cohorts</SelectItem>{cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            <DataAsOf organizationId={selectedOrg} onResynced={() => setRefreshKey((k) => k + 1)} testId="ph-data-as-of" />
          </div>
        )}><HeroAccents /></PageHero>
    )}>
      <div className={cn('space-y-6', isDark ? 'home-dark-scope' : 'mc-light')} data-testid="performance-health-page">
        <PageSubNav items={SUBNAV} />
        <div key={refreshKey} className="contents" data-testid="ph-subview">
        {subView === 'overview' && <PerformanceOverview selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
        {subView === 'outcomes' && <PerformanceOutcomes selectedOrg={selectedOrg} selectedCohort={selectedCohort} onSelectCohort={setSelectedCohort} />}
        {subView === 'drivers' && <PerformanceDrivers selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
        {subView === 'capabilities' && <PerformanceCapabilities selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
        {subView === 'cohorts' && <PerformanceCohorts selectedOrg={selectedOrg} selectedCohort={selectedCohort} onSelectCohort={setSelectedCohort} />}
        {subView === 'influence' && <PerformanceInfluence selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
        {subView === 'evidence' && <PerformanceEvidence selectedOrg={selectedOrg} selectedCohort={selectedCohort} />}
        {subView === 'history' && <PerformanceHistory selectedOrg={selectedOrg} selectedCohort={selectedCohort} orgName={organizations.find((o) => o.id === selectedOrg)?.name || viewAsOrg?.name || rbac?.organization_name || ''} cohortName={cohorts.find((c) => c.id === selectedCohort)?.name || 'All cohorts'} />}
        {PENDING[subView] && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid={`ph-subview-pending-${subView}`}>
            <p className="text-sm font-semibold text-slate-800">{PENDING[subView]} — coming next</p>
            <p className="text-xs text-slate-500 mt-1">This Performance Health view is being built to mirror its Transfer Health sibling. The Overview is live now.</p>
          </div>
        )}
        </div>
      </div>
    </PageShell>
  );
}
